import { useState, useEffect, FC, useRef } from 'react';
import { Grid, TextField, Button, SvgIcon, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, SelectChangeEvent, Stack, Typography, useTheme } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StoresList from './StoresList';
import { Section } from '../../../components/ui/Section';
import { MarketStore, MarketStoreFilterOptions, Instance, Market } from '../../../app/services/api/orderManagementClient';
import { client } from '../../../app/services/api/orderManagementClient';
import Pagination from '../../../components/forms/Pagination';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

type Props = {
  onCreateNew: () => void;
  onFilterChange?: (_instance: string, _market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  editBasePath?: string;
  /** When true, sources instances/markets from the shared InstanceMarketContext/Provider
   * instead of fetching its own copy, defaults to no instance/market selected (rather than
   * "all"), and blocks loading the list until both an instance and a market are picked. Used
   * by the estate-management Sites page so it no longer loads the full store list before the
   * instance field has populated. Defaults to false to preserve existing /stores behaviour. */
  requireInstanceAndMarket?: boolean;
}

const StoresTab: FC<Props> = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm, editBasePath, requireInstanceAndMarket = false }) => {
  const { t } = useTranslation('stores');
  const { logError } = useLogError();
  const theme = useTheme();
  const {
    instances: contextInstances,
    getMarkets: getContextMarkets,
    ensureMarketsLoaded,
    isMarketsLoading: isContextMarketsLoading,
  } = useInstanceMarket();

  const [stores, setStores] = useState<MarketStore[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>(requireInstanceAndMarket ? '' : 'all');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [ownInstances, setOwnInstances] = useState<Instance[]>([]);
  const [ownMarkets, setOwnMarkets] = useState<Market[]>([]);
  const isLoadingMarketsRef = useRef(false);

  // Use the shared context as the single source of instances/markets when required (Sites
  // page); otherwise keep the pre-existing locally-fetched lists (/stores page behaviour).
  const instances = requireInstanceAndMarket ? contextInstances : ownInstances;
  const markets = requireInstanceAndMarket ? getContextMarkets(selectedInstance) : ownMarkets;
  const hasRequiredSelection = !requireInstanceAndMarket || Boolean(selectedInstance && selectedMarket);

  // Snackbar states
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageSize = 20;

  const normalizeKey = (value?: string): string => {
    if (!value) return '';
    return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const resolveInstanceCompanyId = (instanceValue: string): string | undefined => {
    if (!instanceValue || instanceValue === 'all') return undefined;

    if (instances.some((instance) => instance.id?.toString() === instanceValue)) {
      return instanceValue;
    }

    const matched = instances.find((instance) => normalizeKey(instance.name) === normalizeKey(instanceValue));
    return matched?.id?.toString() ?? instanceValue;
  };

  useEffect(() => {
    // The shared context loads instances centrally on its own mount; only fetch our own copy
    // when this tab isn't sourcing instances from that context.
    if (!requireInstanceAndMarket) {
      loadInstances();
    }
  }, [requireInstanceAndMarket]);

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);

  useEffect(() => {
    if (requireInstanceAndMarket) {
      if (selectedInstance) ensureMarketsLoaded(selectedInstance);
      return;
    }

    if (selectedInstance || selectedInstance === 'all') {
      loadMarkets(selectedInstance);
    }
  }, [selectedInstance, requireInstanceAndMarket, ensureMarketsLoaded]);

  useEffect(() => {
    if (requireInstanceAndMarket) {
      // Block loading the list entirely until both an instance and a market are selected.
      if (!hasRequiredSelection) {
        setStores([]);
        setTotalPages(0);
        setTotalItems(0);
        setIsLoading(false);
        return;
      }
      if (isContextMarketsLoading(selectedInstance)) return;
      loadStores();
      return;
    }

    if (isLoadingMarketsRef.current) return;

    if (selectedInstance || selectedInstance === 'all')
    {
      loadStores();
    }
    else{
      setStores([]);
      setTotalPages(0);
      setTotalItems(0);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, selectedInstance, selectedMarket, requireInstanceAndMarket, hasRequiredSelection]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setOwnInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    if (instanceId === 'all') {
      setOwnMarkets([]);
      setSelectedMarket('');
      isLoadingMarketsRef.current = false;
      return;
    }

    isLoadingMarketsRef.current = true;

    try {
      const companyId = resolveInstanceCompanyId(instanceId);

      const response = await client.getMarkets({
        companyId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setOwnMarkets(response.data.items || []);
      }
      else {
        isLoadingMarketsRef.current = false;
      }
    } catch (error) {
      isLoadingMarketsRef.current = false; 
      logError('Failed to load markets');
    }
  };

  useEffect(() => {
    if (requireInstanceAndMarket) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      isLoadingMarketsRef.current = false;
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
      } else {
        loadStores();
      }
    } 
    else {
      isLoadingMarketsRef.current = false;
      if (selectedInstance || selectedInstance === 'all') {
        loadStores();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets, requireInstanceAndMarket]);

  const loadStores = async () => {
    setIsLoading(true);
    try {
      const companyId = resolveInstanceCompanyId(selectedInstance);

      const filterOptions: MarketStoreFilterOptions = {
        companyId,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      const response = await client.getMarketStores(filterOptions);

      if (response.success && response.data) {
        setStores(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalCount || 0);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      }
    } catch (error) {
      logError('Failed to load stores');
      setErrorMessage(t('loadErrorMessage'));
      setIsSubmittedSuccessfully(false);
      setShowSubmitStatusMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    const newInstance = event.target.value;
    setSelectedInstance(newInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(newInstance, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    const newMarket = event.target.value;
    setSelectedMarket(newMarket);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, newMarket);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    const clearedInstance = requireInstanceAndMarket ? '' : 'all';
    setSelectedInstance(clearedInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(clearedInstance, '', '');
  };

  const onClear = () => {
    handleClear();
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  const maxPages = () => {
    return Math.max(1, totalPages);
  };

  return (
    <>
      <Grid container
        spacing={5}>
        <Grid item
          lg={3}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
          />
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleClear}
            variant='secondary'
            size='lg'
          >
            {t('clear')}
          </Button>
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleSearch}
            size='lg'
            startIcon={<SvgIcon><Search /></SvgIcon>}
          >
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByInstance')}
            </InputLabel>
            <Select
              value={
                selectedInstance === 'all' ||
                instances.some((instance) =>
                  instance.id?.toString() === selectedInstance ||
                  normalizeKey(instance.name) === normalizeKey(selectedInstance),
                )
                  ? selectedInstance
                  : ''
              }
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
              renderValue={(value) => {
                if (value === '') {
                  return <span>{requireInstanceAndMarket ? t('selectInstance') : t('allInstances')}</span>;
                }

                return (
                  instances?.find(
                    (instance) =>
                      instance.id?.toString() === value ||
                      normalizeKey(instance.name) === normalizeKey(String(value)),
                  )?.name || ''
                );
              }}
            >
              {!requireInstanceAndMarket && (
                <MenuItem value="all">
                  {t('allInstances')}
                </MenuItem>
              )}
              {instances.map((instance) => (
                <MenuItem
                  key={instance.id}
                  value={instance.id.toString()}
                >
                  {instance.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByMarket')}
            </InputLabel>
            <Select
              value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
              onChange={handleMarketChange}
              label={t('filterByMarket')}
              displayEmpty
              notched
              disabled={!selectedInstance}
              renderValue={(value) => {
                if (value === '') {
                  return <span>{requireInstanceAndMarket ? t('selectMarket') : t('allMarkets')}</span>;
                }
                return markets.find(m => m.customerTypeId?.toString() === value)?.name || '';
              }}
            >
              {!requireInstanceAndMarket && (
                <MenuItem value="">
                  {t('allMarkets')}
                </MenuItem>
              )}
              {markets.map((market) => (
                <MenuItem
                  key={market.customerTypeId}
                  value={market.customerTypeId.toString()}
                >
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            {requireInstanceAndMarket && !hasRequiredSelection ? (
              <Stack alignItems="center"
                spacing={2}
                sx={{ p: 8 }}>
                <Typography variant="textMD"
                  fontWeight={600}>
                  {t('selectInstanceAndMarketTitle')}
                </Typography>
                <Typography variant="textSM"
                  color={theme.palette.custom.gray[600]}>
                  {t('selectInstanceAndMarketText')}
                </Typography>
              </Stack>
            ) : (
              <>
                <StoresList
                  list={stores}
                  isLoading={isLoading}
                  totalCount={totalItems}
                  onClear={onClear}
                  onCreateNew={onCreateNew}
                  editBasePath={editBasePath}
                />
                {!isLoading && ( <Pagination
                  page={currentPage}
                  setPage={setCurrentPage}
                  maxPages={maxPages()}
                  breakpointForChangeDisplay={120000}
                  useNoDataMessage={false}
                />
                )}
              </>
            )}
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? (errorMessage || t('successMessage')) : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StoresTab;
